import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import AdminLayout from "@/components/admin/AdminLayout";
import { StatusBadge, ProgressBar, Stepper } from "@/components/domain/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { money, num, RAFFLE_STATUS } from "@/lib/format";
import { raffleImg, onImgError } from "@/lib/format";
import { toast } from "sonner";
import { Plus, CheckCircle2, ArrowRight, ArrowLeft, Trophy, Pencil, Upload, Link2, X, ImageIcon, Trash2 } from "lucide-react";

const WIZARD = ["General", "Premio", "Galería", "Boletos", "Precio", "Fecha", "Revisión"];
const BLANK = { name: "", description: "", prize: "", prize_value: 0, total_tickets: 1000, price: 100, draw_date: "", method: "Lotería Nacional", rules: "", status: "active", gallery: [], promotions: [], number_start: 0 };

export default function AdminRaffles() {
  const [raffles, setRaffles] = useState([]);
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const [urlInput, setUrlInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState(BLANK);

  const load = () => api.get("/admin/raffles").then((r) => setRaffles(r.data));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditingId(null); setF(BLANK); setStep(0); setUrlInput(""); setOpen(true); };
  const openEdit = (r) => {
    setEditingId(r.id);
    setF({
      name: r.name || "", description: r.description || "", prize: r.prize || "",
      prize_value: r.prize_value || 0, total_tickets: r.total_tickets || 1000, price: r.price || 100,
      draw_date: r.draw_date ? r.draw_date.slice(0, 10) : "", method: r.method || "Lotería Nacional",
      rules: r.rules || "", status: r.status || "active", gallery: r.gallery || [], promotions: r.promotions || [], number_start: r.number_start ?? 1,
    });
    setStep(0); setUrlInput(""); setOpen(true);
  };

  const addUrl = () => {
    const u = urlInput.trim();
    if (!u) return;
    setF((p) => ({ ...p, gallery: [...p.gallery, u] }));
    setUrlInput("");
  };
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Selecciona un archivo de imagen"); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("Máx. 8 MB por imagen"); return; }
    const fd = new FormData();
    fd.append("file", file);
    const tid = toast.loading("Subiendo imagen...");
    try {
      const r = await api.post("/admin/raffles/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((p) => ({ ...p, gallery: [...p.gallery, r.data.url] }));
      toast.success("Imagen subida", { id: tid });
    } catch {
      toast.error("No se pudo subir la imagen", { id: tid });
    }
    e.target.value = "";
  };
  const removeImg = (i) => setF((p) => ({ ...p, gallery: p.gallery.filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    const payload = { ...f, prize_value: Number(f.prize_value), total_tickets: Number(f.total_tickets), price: Number(f.price), number_start: Number(f.number_start) || 0, draw_date: f.draw_date ? new Date(f.draw_date).toISOString() : null };
    try {
      if (editingId) { await api.put(`/admin/raffles/${editingId}`, payload); toast.success("Sorteo actualizado"); }
      else { await api.post("/admin/raffles", payload); toast.success("Sorteo creado"); }
      setOpen(false); await load();
    } catch { toast.error("No se pudo guardar"); } finally { setSaving(false); }
  };

  const [drawFor, setDrawFor] = useState(null);
  const [manualNum, setManualNum] = useState("");
  const draw = async (id, number) => {
    try {
      const r = await api.post(`/admin/raffles/${id}/draw`, number ? { winning_number: Number(number) } : {});
      toast.success(`Ganador: ${r.data.winner_name} · Boleto #${r.data.winning_number}`, {
        description: "Se creó la tarjeta de ganador. Sube su foto en la sección Ganadores.",
        action: { label: "Ir a Ganadores", onClick: () => nav(`/admin/ganadores?edit=${r.data.winner_id}`) }, duration: 8000 });
      setDrawFor(null); setManualNum(""); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo registrar el resultado"); }
  };

  const canRemove = (r) => !["finished", "live", "cancelled"].includes(r.status);
  const remove = async (r) => {
    const msg = r.sold > 0
      ? `"${r.name}" ya tiene ${r.sold} boletos vendidos. Se marcará como CANCELADO y se cancelarán los pagos pendientes. ¿Continuar?`
      : `¿Eliminar definitivamente "${r.name}"? Esta acción no se puede deshacer.`;
    if (!window.confirm(msg)) return;
    try {
      const res = await api.delete(`/admin/raffles/${r.id}`);
      toast.success(res.data.action === "deleted" ? "Sorteo eliminado" : "Sorteo cancelado");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo eliminar"); }
  };

  return (
    <AdminLayout title="Sorteos">
      <div className="mb-6 flex items-center justify-between">
        <p className="text-sm text-slate-500">{raffles.length} sorteos en tu organización</p>
        <Button onClick={openCreate} className="rounded-full bg-brand hover:bg-brand-hover" data-testid="new-raffle-btn"><Plus className="mr-1.5 h-4 w-4" />Nuevo sorteo</Button>
      </div>

      {/* Desktop table */}
      <div className="hidden overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm md:block">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="border-b border-slate-100 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-400">
            <tr><th className="px-5 py-3">Sorteo</th><th className="px-5 py-3">Estado</th><th className="px-5 py-3">Precio</th><th className="px-5 py-3 w-48">Progreso</th><th className="px-5 py-3 text-right">Acciones</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {raffles.map((r) => (
              <tr key={r.id} data-testid={`raffle-row-${r.id}`}>
                <td className="px-5 py-4"><div className="flex items-center gap-3"><img src={raffleImg(r)} onError={onImgError} alt="" className="h-10 w-14 rounded-lg object-cover" /><div><div className="font-semibold text-slate-900">{r.name}</div><div className="text-xs text-slate-400">{r.gallery?.length || 0} imagen(es)</div></div></div></td>
                <td className="px-5 py-4"><StatusBadge {...(RAFFLE_STATUS[r.status] || RAFFLE_STATUS.active)} /></td>
                <td className="px-5 py-4 font-mono">{money(r.price)}</td>
                <td className="px-5 py-4"><ProgressBar value={r.progress} /><div className="mt-1 font-mono text-xs text-slate-400">{r.progress}% · {num(r.sold)}/{num(r.total_tickets)}</div></td>
                <td className="px-5 py-4">
                  <div className="flex items-center justify-end gap-2">
                    <Button size="sm" variant="outline" onClick={() => openEdit(r)} data-testid={`edit-raffle-${r.id}`}><Pencil className="mr-1.5 h-3.5 w-3.5" />Editar</Button>
                    {["active", "sold_out", "live", "finished"].includes(r.status) && <Button size="sm" variant="outline" onClick={() => { setDrawFor(r); setManualNum(r.winning_number ? String(r.winning_number) : ""); }} data-testid={`draw-${r.id}`}><Trophy className="mr-1.5 h-3.5 w-3.5" />{r.status === "finished" ? `Ganador #${r.winning_number}` : "Sortear"}</Button>}
                    {canRemove(r) && <Button size="sm" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" onClick={() => remove(r)} data-testid={`delete-raffle-${r.id}`}><Trash2 className="mr-1.5 h-3.5 w-3.5" />{r.sold > 0 ? "Cancelar" : "Eliminar"}</Button>}
                    {r.status === "finished" && r.winning_number && <span className="font-mono text-sm font-semibold text-emerald-600">#{r.winning_number}</span>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="space-y-3 md:hidden">
        {raffles.map((r) => (
          <div key={r.id} data-testid={`raffle-card-${r.id}`} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <img src={raffleImg(r)} onError={onImgError} alt="" className="h-12 w-16 shrink-0 rounded-lg object-cover" />
              <div className="min-w-0 flex-1">
                <div className="truncate font-semibold text-slate-900">{r.name}</div>
                <div className="text-xs text-slate-400">{r.gallery?.length || 0} imagen(es)</div>
              </div>
              <StatusBadge {...(RAFFLE_STATUS[r.status] || RAFFLE_STATUS.active)} />
            </div>
            <div className="mt-3">
              <ProgressBar value={r.progress} />
              <div className="mt-1 flex justify-between font-mono text-xs text-slate-400"><span>{r.progress}% · {num(r.sold)}/{num(r.total_tickets)}</span><span>{money(r.price)}</span></div>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => openEdit(r)} data-testid={`edit-raffle-m-${r.id}`}><Pencil className="mr-1.5 h-3.5 w-3.5" />Editar</Button>
              {["active", "sold_out", "live", "finished"].includes(r.status) && <Button size="sm" variant="outline" onClick={() => { setDrawFor(r); setManualNum(r.winning_number ? String(r.winning_number) : ""); }} data-testid={`draw-m-${r.id}`}><Trophy className="mr-1.5 h-3.5 w-3.5" />{r.status === "finished" ? `Ganador #${r.winning_number}` : "Sortear"}</Button>}
              {canRemove(r) && <Button size="sm" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" onClick={() => remove(r)} data-testid={`delete-raffle-m-${r.id}`}><Trash2 className="mr-1.5 h-3.5 w-3.5" />{r.sold > 0 ? "Cancelar" : "Eliminar"}</Button>}
              {r.status === "finished" && r.winning_number && <span className="font-mono text-sm font-semibold text-emerald-600">#{r.winning_number}</span>}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingId ? "Editar sorteo" : "Crear sorteo"}</DialogTitle>
            <DialogDescription>Completa cada paso: datos generales, premio, galería de imágenes, boletos, precio y fecha.</DialogDescription>
          </DialogHeader>
          <Stepper steps={WIZARD} current={step} />
          <div className="mt-6 max-h-[52vh] min-h-[220px] space-y-4 overflow-y-auto pr-1">
            {step === 0 && (<>
              <div><Label>Nombre</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1.5" data-testid="wiz-name" /></div>
              <div><Label>Descripción</Label><Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="mt-1.5" rows={4} /></div>
            </>)}
            {step === 1 && (<>
              <div><Label>Premio</Label><Input value={f.prize} onChange={(e) => setF({ ...f, prize: e.target.value })} className="mt-1.5" data-testid="wiz-prize" /></div>
              <div><Label>Valor del premio (MXN)</Label><Input type="number" value={f.prize_value} onChange={(e) => setF({ ...f, prize_value: e.target.value })} className="mt-1.5 font-mono" /></div>
              <div><Label>Bases / método</Label><Textarea value={f.rules} onChange={(e) => setF({ ...f, rules: e.target.value })} className="mt-1.5" rows={3} /></div>
            </>)}
            {step === 2 && (
              <div data-testid="wiz-gallery">
                <Label>Imágenes del artículo a rifar</Label>
                <p className="mt-1 text-xs text-slate-400">Sube fotos (máx. 8 MB c/u) o agrega por URL. La primera imagen es la portada.</p>
                <div className="mt-3 grid grid-cols-3 gap-3 sm:grid-cols-4">
                  {f.gallery.map((g, i) => (
                    <div key={g} className="group relative aspect-square overflow-hidden rounded-lg border border-slate-200">
                      <img src={g} onError={onImgError} alt="" className="h-full w-full object-cover" />
                      {i === 0 && <span className="absolute left-1 top-1 rounded bg-brand px-1.5 py-0.5 text-[9px] font-bold text-white">Portada</span>}
                      <button onClick={() => removeImg(i)} data-testid={`remove-img-${i}`} className="absolute right-1 top-1 grid h-6 w-6 place-items-center rounded-full bg-black/60 text-white opacity-0 transition-opacity group-hover:opacity-100"><X className="h-3.5 w-3.5" /></button>
                    </div>
                  ))}
                  <label className="grid aspect-square cursor-pointer place-items-center rounded-lg border-2 border-dashed border-slate-200 text-slate-400 hover:border-brand hover:text-brand" data-testid="upload-image">
                    <div className="text-center"><Upload className="mx-auto h-5 w-5" /><span className="mt-1 block text-[10px]">Subir</span></div>
                    <input type="file" accept="image/*" className="hidden" onChange={onFile} />
                  </label>
                </div>
                {f.gallery.length === 0 && <div className="mt-3 flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-400"><ImageIcon className="h-4 w-4" />Aún no hay imágenes. Agrega al menos una para la portada.</div>}
                <div className="mt-3 flex gap-2">
                  <div className="relative flex-1"><Link2 className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" /><Input value={urlInput} onChange={(e) => setUrlInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addUrl())} placeholder="https://... URL de imagen" className="pl-9" data-testid="wiz-image-url" /></div>
                  <Button type="button" variant="outline" onClick={addUrl} data-testid="add-image-url">Agregar</Button>
                </div>
              </div>
            )}
            {step === 3 && (
              <div className="space-y-5">
              <div><Label>Cantidad total de boletos</Label><Input type="number" value={f.total_tickets} onChange={(e) => setF({ ...f, total_tickets: e.target.value })} className="mt-1.5 font-mono" data-testid="wiz-tickets" /></div>
              <div>
                <Label>Numeración de folios</Label>
                <div className="mt-1.5 inline-flex rounded-full border border-slate-200 bg-white p-1" data-testid="wiz-number-start">
                  {[[0, "Desde 0000"], [1, "Desde 0001"]].map(([v, l]) => (
                    <button key={v} type="button" onClick={() => setF({ ...f, number_start: v })} data-testid={`wiz-number-start-${v}`}
                      className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${f.number_start === v ? "bg-brand text-white" : "text-slate-500 hover:text-slate-800"}`}>{l}</button>
                  ))}
                </div>
                {(() => { const t = Number(f.total_tickets) || 0; const s = Number(f.number_start) || 0; const len = Math.max(2, String(s + t - 1).length); const p = (n) => String(n).padStart(len, "0");
                  return <p className="mt-2 text-xs text-slate-400" data-testid="wiz-numbering-hint">Numeración del <span className="font-mono">{p(s)}</span> al <span className="font-mono">{p(s + t - 1)}</span> ({p(s)}, {p(s + 1)}, {p(s + 2)}…). Lotería Nacional usa desde 0000. Números únicos garantizados por índice.</p>; })()}
                {editingId && (Number(f.sold) || 0) > 0 && <p className="mt-1 text-xs text-amber-600">No se puede cambiar en un sorteo con boletos vendidos.</p>}
              </div>
              </div>
            )}
            {step === 4 && (
              <div><Label>Precio por boleto (MXN)</Label><Input type="number" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} className="mt-1.5 font-mono" data-testid="wiz-price" /></div>
            )}
            {step === 5 && (<>
              <div><Label>Fecha del sorteo</Label><Input type="date" value={f.draw_date} onChange={(e) => setF({ ...f, draw_date: e.target.value })} className="mt-1.5" data-testid="wiz-date" /></div>
              <div><Label>Método</Label><Select value={f.method} onValueChange={(v) => setF({ ...f, method: v })}><SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Lotería Nacional">Lotería Nacional</SelectItem><SelectItem value="Tómbola">Tómbola en vivo</SelectItem><SelectItem value="Aleatorio certificado">Aleatorio certificado</SelectItem></SelectContent></Select></div>
              <div><Label>Estado</Label><Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}><SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="draft">Borrador</SelectItem><SelectItem value="scheduled">Programado</SelectItem><SelectItem value="active">Activo</SelectItem><SelectItem value="paused">Pausado</SelectItem><SelectItem value="sold_out">Agotado</SelectItem></SelectContent></Select></div>
            </>)}
            {step === 6 && (
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-5 text-sm">
                <div className="mb-3 flex items-center gap-2 text-emerald-600"><CheckCircle2 className="h-4 w-4" />{editingId ? "Listo para guardar cambios" : "Listo para publicar"}</div>
                <div className="mb-3 flex gap-2">{f.gallery.slice(0, 4).map((g) => <img key={g} src={g} onError={onImgError} alt="" className="h-14 w-14 rounded-lg object-cover" />)}</div>
                <dl className="space-y-2">
                  {[["Nombre", f.name], ["Premio", f.prize], ["Imágenes", f.gallery.length], ["Boletos", num(Number(f.total_tickets))], ["Precio", money(Number(f.price))], ["Estado", f.status]].map(([k, v]) => (
                    <div key={k} className="flex justify-between"><dt className="text-slate-500">{k}</dt><dd className="font-medium text-slate-900">{v}</dd></div>
                  ))}
                </dl>
              </div>
            )}
          </div>
          <div className="flex items-center justify-between">
            <Button variant="ghost" onClick={() => step === 0 ? setOpen(false) : setStep(step - 1)}><ArrowLeft className="mr-1.5 h-4 w-4" />{step === 0 ? "Cancelar" : "Atrás"}</Button>
            {step < WIZARD.length - 1 ? (
              <Button onClick={() => setStep(step + 1)} disabled={step === 0 && !f.name} className="rounded-full bg-brand hover:bg-brand-hover" data-testid="wiz-next">Continuar <ArrowRight className="ml-1.5 h-4 w-4" /></Button>
            ) : (
              <Button onClick={save} disabled={saving} className="rounded-full bg-brand hover:bg-brand-hover" data-testid="wiz-save">{editingId ? "Guardar cambios" : "Crear y publicar"}</Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!drawFor} onOpenChange={(o) => { if (!o) setDrawFor(null); }}>
        <DialogContent className="max-w-md" data-testid="draw-dialog">
          <DialogHeader>
            <DialogTitle>{drawFor?.status === "finished" ? "Editar número ganador" : "Definir ganador"}</DialogTitle>
            <DialogDescription>{drawFor?.name} · {num(drawFor?.total_tickets || 0)} boletos{drawFor?.winning_number ? ` · Actual: #${drawFor.winning_number}` : ""}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-lg border border-slate-200 p-4">
              <p className="text-sm font-semibold text-slate-900">Sorteo aleatorio</p>
              <p className="mt-1 text-xs text-slate-500">El sistema elige al azar entre los boletos pagados.</p>
              <Button onClick={() => draw(drawFor.id)} className="mt-3 w-full bg-brand hover:bg-brand-hover" data-testid="draw-random-btn"><Trophy className="mr-1.5 h-4 w-4" />Sortear aleatoriamente</Button>
            </div>
            <div className="rounded-lg border border-slate-200 p-4">
              <p className="text-sm font-semibold text-slate-900">Número manual</p>
              <p className="mt-1 text-xs text-slate-500">Ingresa el boleto ganador definido por otro método (ej. Lotería Nacional).</p>
              <div className="mt-3 flex gap-2">
                <Input type="number" min={drawFor?.number_start ?? 1} max={(drawFor?.number_start ?? 1) + (drawFor?.total_tickets || 0) - 1} value={manualNum} onChange={(e) => setManualNum(e.target.value)} placeholder="Número de boleto" data-testid="draw-manual-input" />
                <Button variant="outline" disabled={!manualNum} onClick={() => draw(drawFor.id, manualNum)} data-testid="draw-manual-btn">Guardar</Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
